import {
  Icon,
  NumberTag
} from '@gripp/shared-ui';
import { cloneElement, useEffect, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View
} from 'react-native';
import {
  Colors
} from '../../themes/colors';
import {
  colors as CropCircleColors
} from '../crop-circle/tokens';

export interface FilterHeadersProps {
  onSearchClose?: () => void;
  onFilterPress: () => void;
  currentSearchKeyword?: string;
  searchComponent?: React.ReactNode;
  filterCount?: number;
  Routes: Record<string, string>;
}

export const FilterHeaders = ({
  onFilterPress,
  onSearchClose,
  currentSearchKeyword,
  searchComponent,
  filterCount,
}: FilterHeadersProps) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(!!currentSearchKeyword);

  // Update isSearchExpanded when currentSearchKeyword changes
  useEffect(() => {
    if (currentSearchKeyword) {
      setIsSearchExpanded(true);
    }
  }, [currentSearchKeyword]);

  const handleSearchPress = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    
    if (onSearchClose) {
      onSearchClose();
    }
  };

  const FilterButton = (
    <Pressable onPress={onFilterPress} style={styles.iconButton}>
      <View style={styles.filterIconContainer}>
        <Icon name="Filter" size={24} color={Colors.black} />
        {filterCount !== undefined && filterCount > 0 && (
          <View style={styles.badgeContainer}>
            <NumberTag number={filterCount} variant="black" />
          </View>
        )}
      </View>
    </Pressable>
  );

  return (
    <View style={[
      styles.iconButtonsContainer,
      isSearchExpanded && styles.searchExpandedContainer
    ]}>
      {isSearchExpanded ? (
        <>
          <View style={styles.searchInputOuterContainer}>
            <Pressable onPress={handleSearchClose} style={styles.closeButton}>
              <Icon name="x" size={16} color={CropCircleColors.neutral[800]} />
            </Pressable>
            <View style={styles.searchInputInnerContainer}>
              {searchComponent
                ? cloneElement(searchComponent as React.ReactElement<{ onBlur?: () => void }>, { onBlur: handleSearchClose })
                : null}
            </View>
          </View>
          {FilterButton}
        </>
      ) : (
        <>
          <Pressable onPress={handleSearchPress} style={styles.iconButton}>
            <Icon name="Search" size={24} color={Colors.black} />
          </Pressable>
          {FilterButton}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  iconButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    ...Platform.select({
      web: {
        columnGap: 16,
      },
    }),
  },
  searchExpandedContainer: {
    ...Platform.select({
      web: {},
      default: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: Colors.white,
        paddingHorizontal: 16,
      }
    }),
  },
  searchInputOuterContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CropCircleColors.neutral[200],
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 8,
    ...Platform.select({
      web: {
        columnGap: 8,
        height: 40,
      },
    }),
  },
  searchInputInnerContainer: {
    width: '90%',
    ...Platform.select({
      web: {
        flex: 1,
      },
    }),
  },
  closeButton: {
    padding: 4,
  },
  iconButton: {
    padding: 4,
    ...Platform.select({
      web: {
        padding: 8,
      },
    }),
  },
  filterIconContainer: {
    position: 'relative',
  },
  badgeContainer: {
    position: 'absolute',
    top: -6,
    right: -6,
  },
});
